/**
 * Expense Management – Firestore data layer
 * Collections: users, projects, expenses, invoices, notifications, settings
 */
import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  runTransaction,
  writeBatch,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { ONGOING_STATUSES, EXPENSE_ADMIN_SEND_DELAY_MS } from '../constants/expenseConstants';

const COLLECTIONS = {
  users: 'users',
  projects: 'projects',
  expenses: 'expenses',
  invoices: 'invoices',
  notifications: 'notifications',
  settings: 'settings',
  counters: 'counters',
};

const ts = () => serverTimestamp();

function getCreatedAtMs(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value.seconds) return value.seconds * 1000;
  if (typeof value === 'string') return new Date(value).getTime() || 0;
  return 0;
}

function sortExpensesByCreatedDesc(rows) {
  return [...rows].sort((a, b) => getCreatedAtMs(b.createdAt) - getCreatedAtMs(a.createdAt));
}

// —— Users ——
export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, COLLECTIONS.users, uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function createUserProfile(uid, data) {
  await setDoc(doc(db, COLLECTIONS.users, uid), {
    email: data.email || '',
    name: data.name || '',
    role: data.role || 'employee',
    createdAt: ts(),
    updatedAt: ts(),
  });
}

// —— Projects ——
export async function createProject(data) {
  const payload = {
    projectId: data.projectId?.trim() || '',
    projectName: data.projectName?.trim() || '',
    startDate: data.startDate || null,
    projectValue: Number(data.projectValue) || 0,
    client: data.client?.trim() || '',
    services: Array.isArray(data.services) ? data.services : [],
    status: data.status || 'Pending',
    billedValue: Number(data.billedValue) || 0,
    completedDate: data.completedDate || null,
    createdAt: ts(),
    updatedAt: ts(),
  };
  const ref = await addDoc(collection(db, COLLECTIONS.projects), payload);
  if (payload.status === 'Completed') {
    try {
      await createNotification('project_completed', { projectDocId: ref.id, projectName: payload.projectName });
    } catch {
      // Non-blocking if notification write fails.
    }
  }
  return ref.id;
}

export async function updateProject(id, data) {
  const updates = { ...data, updatedAt: ts() };
  if (data.projectValue !== undefined) updates.projectValue = Number(data.projectValue) || 0;
  if (data.billedValue !== undefined) updates.billedValue = Number(data.billedValue) || 0;
  if (data.services) updates.services = data.services;
  if (data.status === 'Completed' && !data.completedDate) {
    updates.completedDate = new Date().toISOString().split('T')[0];
  }

  // Check if projectName or projectId is being updated to cascade to expenses
  const docRef = doc(db, COLLECTIONS.projects, id);
  const docSnap = await getDoc(docRef);
  let needsExpenseUpdate = false;
  let newProjectName = data.projectName;
  let newProjectId = data.projectId;

  if (docSnap.exists()) {
    const oldData = docSnap.data();
    if ((data.projectName !== undefined && data.projectName !== oldData.projectName) || 
        (data.projectId !== undefined && data.projectId !== oldData.projectId)) {
      needsExpenseUpdate = true;
      newProjectName = data.projectName !== undefined ? data.projectName : oldData.projectName;
      newProjectId = data.projectId !== undefined ? data.projectId : oldData.projectId;
    }
  }

  await updateDoc(docRef, updates);

  if (needsExpenseUpdate) {
    try {
      const expensesQ = query(collection(db, COLLECTIONS.expenses), where('projectDocId', '==', id));
      const expensesSnap = await getDocs(expensesQ);
      if (!expensesSnap.empty) {
        const batch = writeBatch(db);
        expensesSnap.forEach((expenseDoc) => {
          batch.update(expenseDoc.ref, { 
            projectName: newProjectName, 
            projectId: newProjectId 
          });
        });
        await batch.commit();
      }
    } catch (err) {
      console.error('Failed to cascade project update to expenses:', err);
    }
  }

  if (data.status === 'Completed') {
    try {
      await createNotification('project_completed', { projectDocId: id, projectName: data.projectName });
    } catch {
      // Non-blocking if notification write fails.
    }
  }
}

export async function saveProjectInvoiceDraft(projectDocId, draft) {
  await updateDoc(doc(db, COLLECTIONS.projects, projectDocId), {
    invoiceDraft: {
      poc: draft.poc || '',
      purchaseOrder: draft.purchaseOrder || '',
      additionalRows: draft.additionalRows || [],
      additionalAmount: Number(draft.additionalAmount) || 0,
      updatedAt: new Date().toISOString(),
    },
    billedValue: Number(draft.billedValue) || 0,
    updatedAt: ts(),
  });
}

export async function deleteProject(id) {
  await deleteDoc(doc(db, COLLECTIONS.projects, id));
}

export function subscribeProjects(callback) {
  const q = query(collection(db, COLLECTIONS.projects), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, () => callback([]));
}

export function subscribeOngoingProjects(callback) {
  const q = query(collection(db, COLLECTIONS.projects), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snap) => {
    const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const ongoing = all.filter((p) => ONGOING_STATUSES.includes(p.status));
    ongoing.sort((a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0));
    callback(ongoing);
  }, () => callback([]));
}

// —— Expenses ——
export function getExpenseSendRemainingMs(expense) {
  if (expense.sentToAdmin !== false) return 0;
  if (expense.sendPaused) return EXPENSE_ADMIN_SEND_DELAY_MS;
  if (!expense.adminSendAt) return EXPENSE_ADMIN_SEND_DELAY_MS;
  return Math.max(0, new Date(expense.adminSendAt).getTime() - Date.now());
}

export function isExpenseSendDue(expense) {
  if (expense.sentToAdmin !== false || expense.sendPaused) return false;
  if (!expense.adminSendAt) return false;
  return new Date(expense.adminSendAt).getTime() <= Date.now();
}

export function isExpenseVisibleToAdmin(expense) {
  return expense.sentToAdmin !== false;
}

export function shouldHideFromEmployeeHistory(expense, projectStatus) {
  return expense.approved === true && projectStatus === 'Completed';
}

export async function createExpense(data, userId) {
  const approved = data.approved ?? false;
  const sentToAdmin = data.sentToAdmin ?? approved;
  const payload = {
    expenseId: data.expenseId || '',
    employeeId: userId || data.employeeId || '',
    employeeName: data.employeeName || '',
    projectDocId: data.projectDocId || '',
    projectId: data.projectId || '',
    projectName: data.projectName || '',
    expense: data.expense?.trim() || '',
    amount: Number(data.amount) || 0,
    date: data.date || new Date().toISOString().split('T')[0],
    status: data.status || 'Paid',
    serviceProvidedPersonName: data.serviceProvidedPersonName?.trim() || '',
    approved,
    sentToAdmin,
    sendPaused: false,
    createdAt: ts(),
    updatedAt: ts(),
  };
  if (sentToAdmin === false) {
    payload.adminSendAt = new Date(Date.now() + EXPENSE_ADMIN_SEND_DELAY_MS).toISOString();
  }
  const ref = await addDoc(collection(db, COLLECTIONS.expenses), payload);
  if (sentToAdmin && !approved) {
    try {
      await createNotification('expense_pending', { expenseDocId: ref.id, projectName: payload.projectName });
    } catch {
      // Employees cannot write notifications; expense is still saved.
    }
  }
  return ref.id;
}

export async function sendExpenseToAdmin(id, projectName) {
  const expenseRef = doc(db, COLLECTIONS.expenses, id);
  const snap = await getDoc(expenseRef);
  if (!snap.exists() || snap.data().sentToAdmin !== false || snap.data().sendPaused) return;
  await updateDoc(expenseRef, { sentToAdmin: true, updatedAt: ts() });
  try {
    await createNotification('expense_pending', {
      expenseDocId: id,
      projectName: projectName || snap.data().projectName || '',
    });
  } catch {
    // Non-blocking if notification write fails.
  }
}

export async function pauseExpenseSend(id) {
  await updateDoc(doc(db, COLLECTIONS.expenses, id), {
    sendPaused: true,
    updatedAt: ts(),
  });
}

export async function updateExpense(id, data) {
  const updates = { ...data, updatedAt: ts() };
  if (data.amount !== undefined) updates.amount = Number(data.amount) || 0;
  if (data.serviceProvidedPersonName !== undefined) {
    updates.serviceProvidedPersonName = data.serviceProvidedPersonName?.trim() || '';
  }
  await updateDoc(doc(db, COLLECTIONS.expenses, id), updates);
}

const AUDIT_FIELDS = [
  'expense', 'amount', 'date', 'status', 'serviceProvidedPersonName',
  'projectDocId', 'projectId', 'projectName',
];

/** Admin edit with audit trail — stores editedBy, editedDate, original/updated values */
export async function adminUpdateExpense(id, data, adminInfo = {}) {
  const expenseRef = doc(db, COLLECTIONS.expenses, id);
  const snap = await getDoc(expenseRef);
  if (!snap.exists()) throw new Error('Expense not found');

  const existing = snap.data();
  const changes = [];
  AUDIT_FIELDS.forEach((field) => {
    if (data[field] === undefined) return;
    let newVal = data[field];
    if (field === 'amount') newVal = Number(newVal) || 0;
    if (field === 'serviceProvidedPersonName') newVal = newVal?.trim() || '';
    const oldVal = existing[field];
    if (String(oldVal ?? '') !== String(newVal ?? '')) {
      changes.push({ field, originalValue: oldVal ?? '', updatedValue: newVal ?? '' });
    }
  });

  const updates = { ...data, updatedAt: ts() };
  if (data.amount !== undefined) updates.amount = Number(data.amount) || 0;
  if (data.serviceProvidedPersonName !== undefined) {
    updates.serviceProvidedPersonName = data.serviceProvidedPersonName?.trim() || '';
  }

  if (changes.length > 0) {
    const entry = {
      editedBy: adminInfo.name || adminInfo.email || 'Admin',
      editedDate: new Date().toISOString(),
      changes,
    };
    const history = Array.isArray(existing.editHistory) ? [...existing.editHistory, entry] : [entry];
    updates.editHistory = history;
    updates.adminEdited = true;
    updates.lastEditedBy = entry.editedBy;
    updates.lastEditedAt = entry.editedDate;
  }

  await updateDoc(expenseRef, updates);
}

export async function bulkDeleteExpenses(ids) {
  await Promise.all(ids.map((id) => deleteDoc(doc(db, COLLECTIONS.expenses, id))));
}

export async function approveExpense(id) {
  await updateDoc(doc(db, COLLECTIONS.expenses, id), { approved: true, updatedAt: ts() });
}

export async function deleteExpense(id) {
  await deleteDoc(doc(db, COLLECTIONS.expenses, id));
}

export function subscribeExpenses(callback, employeeId = null, onError = null) {
  const q = employeeId
    ? query(collection(db, COLLECTIONS.expenses), where('employeeId', '==', employeeId))
    : query(collection(db, COLLECTIONS.expenses), orderBy('createdAt', 'desc'));

  return onSnapshot(q, (snap) => {
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(employeeId ? sortExpensesByCreatedDesc(rows) : rows);
  }, (err) => {
    console.error('subscribeExpenses error:', err);
    onError?.(err);
    callback([]);
  });
}

export function getProjectTotalExpenses(expenses, projectDocId) {
  return expenses
    .filter((e) => e.projectDocId === projectDocId)
    .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
}

export function getProjectBalance(project, expenses) {
  const total = getProjectTotalExpenses(expenses, project.id);
  return (Number(project.projectValue) || 0) - total;
}

export function getProjectRevenue(project) {
  return Number(project.billedValue) || Number(project.projectValue) || 0;
}

export function getProjectProfit(project, expenses) {
  return getProjectRevenue(project) - getProjectTotalExpenses(expenses, project.id);
}

function parseMonthKey(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function parseYearKey(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return String(d.getFullYear());
}

/** Company-level financial aggregations (monthly / quarterly / yearly) */
export function computeFinancialAggregates(projects, expenses, invoices = []) {
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const currentYear = String(now.getFullYear());
  const currentQuarter = `${currentYear}-Q${Math.floor(now.getMonth() / 3) + 1}`;

  const monthlyExpenses = {};
  const yearlyExpenses = {};
  const quarterlyExpenses = {};

  expenses.forEach((e) => {
    const amt = Number(e.amount) || 0;
    const monthKey = parseMonthKey(e.date);
    const yearKey = parseYearKey(e.date);
    if (monthKey) {
      monthlyExpenses[monthKey] = (monthlyExpenses[monthKey] || 0) + amt;
      const [y, m] = monthKey.split('-');
      const qKey = `${y}-Q${Math.ceil(Number(m) / 3)}`;
      quarterlyExpenses[qKey] = (quarterlyExpenses[qKey] || 0) + amt;
    }
    if (yearKey) yearlyExpenses[yearKey] = (yearlyExpenses[yearKey] || 0) + amt;
  });

  const monthlyIncome = {};
  const yearlyIncome = {};
  const quarterlyIncome = {};

  projects.forEach((p) => {
    const revenue = getProjectRevenue(p);
    const inv = invoices.find((i) => i.projectDocId === p.id);
    const dateStr = inv?.date || p.completedDate || p.startDate;
    const monthKey = parseMonthKey(dateStr);
    const yearKey = parseYearKey(dateStr);
    if (monthKey) {
      monthlyIncome[monthKey] = (monthlyIncome[monthKey] || 0) + revenue;
      const [y, m] = monthKey.split('-');
      const qKey = `${y}-Q${Math.ceil(Number(m) / 3)}`;
      quarterlyIncome[qKey] = (quarterlyIncome[qKey] || 0) + revenue;
    }
    if (yearKey) yearlyIncome[yearKey] = (yearlyIncome[yearKey] || 0) + revenue;
  });

  const sumIncome = (map, key) => map[key] || 0;
  const sumExpenses = (map, key) => map[key] || 0;

  const monthlyIncomeVal = sumIncome(monthlyIncome, currentMonth);
  const monthlyExpenseVal = sumExpenses(monthlyExpenses, currentMonth);
  const yearlyIncomeVal = sumIncome(yearlyIncome, currentYear);
  const yearlyExpenseVal = sumExpenses(yearlyExpenses, currentYear);
  const quarterlyIncomeVal = sumIncome(quarterlyIncome, currentQuarter);
  const quarterlyExpenseVal = sumExpenses(quarterlyExpenses, currentQuarter);

  const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevMonth = `${prevMonthDate.getFullYear()}-${String(prevMonthDate.getMonth() + 1).padStart(2, '0')}`;
  const prevYear = String(now.getFullYear() - 1);

  const growthPct = (current, previous) => {
    if (!previous) return current ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const lossProjects = projects
    .map((p) => {
      const revenue = getProjectRevenue(p);
      const totalExpense = getProjectTotalExpenses(expenses, p.id);
      const profit = revenue - totalExpense;
      return {
        ...p,
        revenue,
        totalExpense,
        profit,
        lossAmount: profit < 0 ? Math.abs(profit) : 0,
      };
    })
    .filter((p) => p.profit < 0)
    .sort((a, b) => b.lossAmount - a.lossAmount);

  const expenseCategories = expenses.reduce((acc, e) => {
    const cat = e.expense?.trim() || 'Uncategorized';
    acc[cat] = (acc[cat] || 0) + (Number(e.amount) || 0);
    return acc;
  }, {});

  return {
    currentMonth,
    currentYear,
    currentQuarter,
    monthly: {
      income: monthlyIncomeVal,
      expenses: monthlyExpenseVal,
      profitLoss: monthlyIncomeVal - monthlyExpenseVal,
      incomeGrowth: growthPct(monthlyIncomeVal, sumIncome(monthlyIncome, prevMonth)),
      expenseGrowth: growthPct(monthlyExpenseVal, sumExpenses(monthlyExpenses, prevMonth)),
    },
    yearly: {
      income: yearlyIncomeVal,
      expenses: yearlyExpenseVal,
      profitLoss: yearlyIncomeVal - yearlyExpenseVal,
      incomeGrowth: growthPct(yearlyIncomeVal, sumIncome(yearlyIncome, prevYear)),
      expenseGrowth: growthPct(yearlyExpenseVal, sumExpenses(yearlyExpenses, prevYear)),
    },
    quarterly: {
      income: quarterlyIncomeVal,
      expenses: quarterlyExpenseVal,
      profitLoss: quarterlyIncomeVal - quarterlyExpenseVal,
    },
    monthlyIncome,
    monthlyExpenses,
    yearlyIncome,
    yearlyExpenses,
    lossProjects,
    expenseCategories,
    totalCompanyProfit: projects.reduce((s, p) => s + getProjectRevenue(p), 0)
      - expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0),
  };
}

export function formatEditTooltip(expense) {
  if (!expense?.adminEdited && !expense?.lastEditedAt) return '';
  const date = expense.lastEditedAt ? new Date(expense.lastEditedAt) : null;
  const formatted = date
    ? `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    : '';
  return `Edited by ${expense.lastEditedBy || 'Admin'} on ${formatted}`;
}

// —— Invoices ——
export async function getNextInvoiceNumber() {
  const year = new Date().getFullYear();
  const counterRef = doc(db, COLLECTIONS.counters, `invoice_${year}`);
  return runTransaction(db, async (transaction) => {
    const snap = await transaction.get(counterRef);
    const current = snap.exists() ? snap.data().count || 0 : 0;
    const next = current + 1;
    transaction.set(counterRef, { count: next, year }, { merge: true });
    return `INV-${year}-${String(next).padStart(4, '0')}`;
  });
}

export async function createInvoice(data) {
  const payload = {
    invoiceId: data.invoiceId || '',
    projectDocId: data.projectDocId || '',
    projectId: data.projectId || '',
    projectName: data.projectName || '',
    client: data.client || '',
    subtotal: Number(data.subtotal) || 0,
    cgst: Number(data.cgst) || 0,
    sgst: Number(data.sgst) || 0,
    igst: Number(data.igst) || 0,
    grandTotal: Number(data.grandTotal) || 0,
    additionalAmount: Number(data.additionalAmount) || 0,
    additionalRows: data.additionalRows || [],
    poc: data.poc || '',
    purchaseOrder: data.purchaseOrder || '',
    pdfUrl: data.pdfUrl || '',
    date: data.date || new Date().toISOString().split('T')[0],
    createdAt: ts(),
    updatedAt: ts(),
  };
  const ref = await addDoc(collection(db, COLLECTIONS.invoices), payload);
  return ref.id;
}

export async function deleteInvoice(id) {
  await deleteDoc(doc(db, COLLECTIONS.invoices, id));
}

export function subscribeInvoices(callback) {
  const q = query(collection(db, COLLECTIONS.invoices), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, () => callback([]));
}

export async function uploadInvoicePdf(blob, invoiceNumber) {
  return new Promise((resolve, reject) => {
    console.log('Starting PDF upload to Firebase Storage...', invoiceNumber);
    const storageRef = ref(storage, `invoices/${invoiceNumber}.pdf`);
    
    // Add a timeout of 10 seconds to catch hanging uploads
    const timeout = setTimeout(() => {
      reject(new Error('PDF Upload timed out after 10 seconds. Check Firebase Storage rules or network connection.'));
    }, 10000);

    uploadBytes(storageRef, blob, { contentType: 'application/pdf' })
      .then(() => {
        console.log('Upload bytes complete. Getting download URL...');
        return getDownloadURL(storageRef);
      })
      .then((url) => {
        clearTimeout(timeout);
        console.log('Got download URL:', url);
        resolve(url);
      })
      .catch((error) => {
        clearTimeout(timeout);
        console.error('PDF upload failed:', error);
        reject(error);
      });
  });
}

// —— Settings ——
export function subscribeSettings(callback) {
  const settingsRef = doc(db, COLLECTIONS.settings, 'company');
  return onSnapshot(settingsRef, (snap) => {
    callback(snap.exists() ? snap.data() : {});
  }, () => callback({}));
}

export async function saveSettings(data) {
  await setDoc(doc(db, COLLECTIONS.settings, 'company'), { ...data, updatedAt: ts() }, { merge: true });
}

// —— Notifications ——
async function createNotification(type, data) {
  await addDoc(collection(db, COLLECTIONS.notifications), {
    type,
    ...data,
    read: false,
    createdAt: ts(),
  });
}

export function subscribePendingExpenseCount(callback) {
  const q = query(collection(db, COLLECTIONS.expenses), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snap) => {
    callback(
      snap.docs.filter((d) => {
        const data = d.data();
        return data.sentToAdmin !== false && data.approved === false;
      }).length
    );
  }, () => callback(0));
}
